// src/pages/SystemPreferencesPage.tsx — Sign-In System (Build 1).
//
// Admin screen for the hub_system_prefs KV store (prototype State 10) plus the
// duty role + location catalogue editors (State 16). SSO admin only — the page
// renders an access notice for anyone else, and every write goes through
// /api/access/prefs or /api/timesheet/{roles,locations} (both requireSsoAdmin).
//
// Pref edits are collected into a draft and saved as one atomic PATCH batch.
// Catalogue rows save individually. Sage accents per the prototype.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch, ApiError } from "../api";
import { useCurrentUser } from "../auth";
import { C, F } from "../theme";
import { Card, Section, TopBar, PermissionLoading } from "../components/ui";

type PrefType = "text" | "int" | "bool";
interface PrefRow {
  key: string;
  type: PrefType;
  value: string | number | boolean | null;
  version: number;
  updatedAt: string;
  recentAudit: Array<{ id: number; value: unknown; editedBy: string; editedAt: string }>;
}
interface PrefsResponse { prefs: PrefRow[]; stats: { medicalConfirmationsThisWeek: number } }

interface RoleRow { id: number; code: string; displayName: string; defaultLocationCode: string | null; sortOrder: number; archived: boolean; codeLocked: boolean }
interface LocationRow { id: number; code: string; displayName: string; isOnTrain: boolean; sortOrder: number; archived: boolean }

export function SystemPreferencesPage() {
  const { me } = useCurrentUser();
  const navigate = useNavigate();
  const [prefs, setPrefs] = useState<PrefRow[] | null>(null);
  const [stats, setStats] = useState<{ medicalConfirmationsThisWeek: number } | null>(null);
  const [roles, setRoles] = useState<RoleRow[]>([]);
  const [locations, setLocations] = useState<LocationRow[]>([]);
  const [draft, setDraft] = useState<Record<string, string | number | boolean>>({});
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);

  const flash = useCallback((m: string) => {
    setToast(m);
    window.setTimeout(() => setToast((t) => (t === m ? null : t)), 3000);
  }, []);

  const load = useCallback(async () => {
    try {
      const [p, r, l] = await Promise.all([
        apiFetch<PrefsResponse>("/api/access/prefs"),
        apiFetch<{ roles: RoleRow[] }>("/api/timesheet/roles"),
        apiFetch<{ locations: LocationRow[] }>("/api/timesheet/locations"),
      ]);
      setPrefs(p.prefs);
      setStats(p.stats);
      setRoles(r.roles);
      setLocations(l.locations);
    } catch (e) {
      setLoadErr(e instanceof ApiError ? e.message : "Couldn't load preferences.");
    }
  }, []);

  useEffect(() => { if (me?.isSsoAdmin) void load(); }, [me?.isSsoAdmin, load]);

  const byKey = useMemo(() => {
    const m = new Map<string, PrefRow>();
    (prefs ?? []).forEach((p) => m.set(p.key, p));
    return m;
  }, [prefs]);

  const valueOf = (key: string): string | number | boolean | null => {
    if (key in draft) return draft[key];
    return byKey.get(key)?.value ?? null;
  };
  const setVal = (key: string, v: string | number | boolean) => setDraft((d) => ({ ...d, [key]: v }));

  const dirtyKeys = Object.keys(draft).filter((k) => {
    const orig = byKey.get(k)?.value;
    return draft[k] !== orig;
  });

  const save = async () => {
    const changes = dirtyKeys.map((key) => ({ key, value: draft[key] }));
    if (changes.length === 0) return;
    setSaving(true);
    try {
      await apiFetch("/api/access/prefs", { method: "PATCH", body: JSON.stringify({ changes }) });
      setDraft({});
      await load();
      flash(`Saved ${changes.length} change${changes.length === 1 ? "" : "s"}`);
    } catch (e) {
      flash(e instanceof ApiError ? e.message : "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  if (me && !me.isSsoAdmin) {
    return (
      <div>
        <TopBar title="System Preferences" sub="Admin only" onBack={() => navigate("/apps")} />
        <div style={{ padding: 24 }}>
          <Card>
            <div style={{ fontFamily: F.body, fontWeight: 700, color: C.inkSoft }}>
              System Preferences is available to NYMR Entra (SSO) admins only.
            </div>
          </Card>
        </div>
      </div>
    );
  }
  if (!me || prefs === null) {
    if (loadErr) {
      return (<div><TopBar title="System Preferences" onBack={() => navigate("/apps")} /><div style={{ padding: 24 }}><Card><div style={{ color: C.signalDk, fontFamily: F.body, fontWeight: 700 }}>{loadErr}</div></Card></div></div>);
    }
    return <PermissionLoading title="System Preferences" />;
  }

  const decl = byKey.get("duty.medical_declaration_text");
  const lastEdit = decl?.recentAudit[0];

  return (
    <div style={{ paddingBottom: 96 }}>
      <TopBar title="System Preferences" sub="Feature settings & catalogues" onBack={() => navigate("/apps")} />
      <div style={{ padding: "18px 16px 0", display: "flex", flexDirection: "column", gap: 18 }}>

        {/* 1. Medical fitness declaration */}
        <div>
          <Section title="Medical fitness declaration" sub="Snapshotted into every sign-in" />
          <Card>
            <textarea
              value={String(valueOf("duty.medical_declaration_text") ?? "")}
              onChange={(e) => setVal("duty.medical_declaration_text", e.target.value)}
              rows={5}
              style={{ width: "100%", boxSizing: "border-box", fontFamily: F.serif, fontSize: 14.5, lineHeight: 1.5, color: C.ink, border: `1px solid ${C.hairline}`, borderRadius: 12, padding: 12, resize: "vertical", background: C.paperSoft }}
            />
            <div style={{ fontFamily: F.mono, fontSize: 10.5, color: C.inkMute, marginTop: 8, letterSpacing: 0.4 }}>
              v{decl?.version ?? 0}
              {lastEdit && ` · last edited ${lastEdit.editedBy} · ${formatDate(lastEdit.editedAt)}`}
              {stats && ` · ${stats.medicalConfirmationsThisWeek} confirmations this week`}
            </div>
          </Card>
        </div>

        {/* 2. Reminders & auto sign-off */}
        <div>
          <Section title="Reminders & auto sign-off" sub="Long shifts" />
          <Card>
            <ToggleRow label="Long-shift reminder" checked={!!valueOf("duty.remind_enabled")} onChange={(v) => setVal("duty.remind_enabled", v)} />
            <NumberRow label="Remind after (minutes)" value={Number(valueOf("duty.remind_at_minutes") ?? 360)} onChange={(v) => setVal("duty.remind_at_minutes", v)} />
            <Divider />
            <ToggleRow label="Auto sign-off" checked={!!valueOf("duty.auto_signoff_enabled")} onChange={(v) => setVal("duty.auto_signoff_enabled", v)} />
            <NumberRow label="Auto sign-off after (minutes)" value={Number(valueOf("duty.auto_signoff_at_minutes") ?? 480)} onChange={(v) => setVal("duty.auto_signoff_at_minutes", v)} />
          </Card>
        </div>

        {/* 3. Lone Working */}
        <div>
          <Section title="Lone Working" sub="Safety check-in" />
          <Card>
            <ToggleRow label="Lone Working enabled" checked={!!valueOf("lone_working.enabled")} onChange={(v) => setVal("lone_working.enabled", v)} />
            <TextRow label="Window options (JSON minutes)" value={String(valueOf("lone_working.windows_minutes") ?? "[120,240,360,480]")} onChange={(v) => setVal("lone_working.windows_minutes", v)} mono />
          </Card>
        </div>

        {/* 4. Group QuickMessage defaults (Build 2 surfaces; keys seed in Build 1) */}
        <div>
          <Section title="Group QuickMessage defaults" sub="Build 2 surface" />
          <Card>
            <ToggleRow label="Group QuickMessage enabled" checked={!!valueOf("group_qm.enabled")} onChange={(v) => setVal("group_qm.enabled", v)} />
            <NumberRow label="Auto-close idle (minutes)" value={Number(valueOf("group_qm.auto_close_idle_minutes") ?? 90)} onChange={(v) => setVal("group_qm.auto_close_idle_minutes", v)} />
          </Card>
        </div>

        {/* 5. Feature flags */}
        <div>
          <Section title="Feature flags" sub="Hide a tile + suppress its surface" />
          <Card>
            <ToggleRow label="Sign In for Duty" checked={!!valueOf("features.duty_signin")} onChange={(v) => setVal("features.duty_signin", v)} />
            <ToggleRow label="Sign In Timesheet app" checked={!!valueOf("features.timesheet_app")} onChange={(v) => setVal("features.timesheet_app", v)} />
            <ToggleRow label="Group QuickMessage" checked={!!valueOf("features.group_quick_message")} onChange={(v) => setVal("features.group_quick_message", v)} />
          </Card>
        </div>

        {/* 6. Roles editor */}
        <RolesEditor roles={roles} locations={locations} onChanged={load} flash={flash} />

        {/* 7. Locations editor */}
        <LocationsEditor locations={locations} onChanged={load} flash={flash} />
      </div>

      {dirtyKeys.length > 0 && (
        <div style={{ position: "fixed", left: 0, right: 0, bottom: "calc(0px + env(safe-area-inset-bottom))", padding: 12, display: "flex", justifyContent: "center", zIndex: 60, background: "linear-gradient(0deg, rgba(251,246,232,0.96), rgba(251,246,232,0))" }}>
          <button onClick={save} disabled={saving} style={{ maxWidth: 520, width: "100%", minHeight: 48, background: C.sageDk, color: "#fff", border: "none", borderRadius: 12, fontFamily: F.body, fontWeight: 800, fontSize: 15, cursor: "pointer", opacity: saving ? 0.7 : 1 }}>
            {saving ? "Saving…" : `Save ${dirtyKeys.length} change${dirtyKeys.length === 1 ? "" : "s"}`}
          </button>
        </div>
      )}
      {toast && <Toast text={toast} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Roles + Locations editors
// ---------------------------------------------------------------------------

function RolesEditor({ roles, locations, onChanged, flash }: { roles: RoleRow[]; locations: LocationRow[]; onChanged: () => Promise<void>; flash: (m: string) => void }) {
  const [adding, setAdding] = useState(false);
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [dlc, setDlc] = useState<string>("");

  const activeLocations = locations.filter((l) => !l.archived);

  const addRole = async () => {
    try {
      await apiFetch("/api/timesheet/roles", { method: "POST", body: JSON.stringify({ code, displayName: name, defaultLocationCode: dlc || null }) });
      setAdding(false); setCode(""); setName(""); setDlc("");
      await onChanged(); flash("Role added");
    } catch (e) { flash(e instanceof ApiError ? e.message : "Couldn't add role"); }
  };
  const archive = async (id: number) => {
    try { await apiFetch(`/api/timesheet/roles/${id}`, { method: "DELETE" }); await onChanged(); flash("Role archived"); }
    catch (e) { flash(e instanceof ApiError ? e.message : "Couldn't archive"); }
  };
  const saveDefault = async (id: number, value: string) => {
    try { await apiFetch(`/api/timesheet/roles/${id}`, { method: "PATCH", body: JSON.stringify({ defaultLocationCode: value || null }) }); await onChanged(); }
    catch (e) { flash(e instanceof ApiError ? e.message : "Couldn't update"); }
  };

  return (
    <div>
      <Section title="Duty roles" sub="Per-user grants live in User Access" />
      <Card>
        {roles.filter((r) => !r.archived).map((r) => (
          <div key={r.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: `1px solid ${C.hairlineLt}` }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: F.body, fontWeight: 700, fontSize: 14, color: C.ink }}>{r.displayName}</div>
              <div style={{ fontFamily: F.mono, fontSize: 10, color: C.inkMute }}>{r.code}{r.codeLocked ? " · locked" : ""}</div>
            </div>
            <select value={r.defaultLocationCode ?? ""} onChange={(e) => saveDefault(r.id, e.target.value)} style={selectStyle()}>
              <option value="">(no default)</option>
              {activeLocations.map((l) => <option key={l.code} value={l.code}>{l.displayName}</option>)}
            </select>
            <button onClick={() => archive(r.id)} style={miniBtn(C.signalDk)}>Archive</button>
          </div>
        ))}
        {adding ? (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, paddingTop: 12 }}>
            <input placeholder="code (a-z_)" value={code} onChange={(e) => setCode(e.target.value)} style={inputStyle(120)} />
            <input placeholder="Display name" value={name} onChange={(e) => setName(e.target.value)} style={inputStyle(160)} />
            <select value={dlc} onChange={(e) => setDlc(e.target.value)} style={selectStyle()}>
              <option value="">(default location)</option>
              {activeLocations.map((l) => <option key={l.code} value={l.code}>{l.displayName}</option>)}
            </select>
            <button onClick={addRole} style={miniBtn(C.sageDk)}>Add</button>
            <button onClick={() => setAdding(false)} style={miniBtn(C.inkMute)}>Cancel</button>
          </div>
        ) : (
          <button onClick={() => setAdding(true)} style={{ ...dashedAdd() }}>+ Add a new role</button>
        )}
      </Card>
    </div>
  );
}

function LocationsEditor({ locations, onChanged, flash }: { locations: LocationRow[]; onChanged: () => Promise<void>; flash: (m: string) => void }) {
  const [adding, setAdding] = useState(false);
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [onTrain, setOnTrain] = useState(false);

  const addLocation = async () => {
    try {
      await apiFetch("/api/timesheet/locations", { method: "POST", body: JSON.stringify({ code, displayName: name, isOnTrain: onTrain }) });
      setAdding(false); setCode(""); setName(""); setOnTrain(false);
      await onChanged(); flash("Location added");
    } catch (e) { flash(e instanceof ApiError ? e.message : "Couldn't add location"); }
  };
  const archive = async (id: number) => {
    try { await apiFetch(`/api/timesheet/locations/${id}`, { method: "DELETE" }); await onChanged(); flash("Location archived"); }
    catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        const used = (e.body as { usedAsDefaultBy?: string[] })?.usedAsDefaultBy ?? [];
        flash(`In use as default by: ${used.join(", ")}`);
      } else flash(e instanceof ApiError ? e.message : "Couldn't archive");
    }
  };
  const toggleTrain = async (id: number, v: boolean) => {
    try { await apiFetch(`/api/timesheet/locations/${id}`, { method: "PATCH", body: JSON.stringify({ isOnTrain: v }) }); await onChanged(); }
    catch (e) { flash(e instanceof ApiError ? e.message : "Couldn't update"); }
  };

  return (
    <div>
      <Section title="Locations" sub="Stations + on-train" />
      <Card>
        {locations.filter((l) => !l.archived).map((l) => (
          <div key={l.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: `1px solid ${C.hairlineLt}` }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: F.body, fontWeight: 700, fontSize: 14, color: C.ink }}>{l.displayName}</div>
              <div style={{ fontFamily: F.mono, fontSize: 10, color: C.inkMute }}>{l.code}</div>
            </div>
            <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontFamily: F.body, fontSize: 12, color: C.inkSoft }}>
              <input type="checkbox" checked={l.isOnTrain} onChange={(e) => toggleTrain(l.id, e.target.checked)} /> On train
            </label>
            <button onClick={() => archive(l.id)} style={miniBtn(C.signalDk)}>Archive</button>
          </div>
        ))}
        {adding ? (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, paddingTop: 12, alignItems: "center" }}>
            <input placeholder="code (a-z_)" value={code} onChange={(e) => setCode(e.target.value)} style={inputStyle(120)} />
            <input placeholder="Display name" value={name} onChange={(e) => setName(e.target.value)} style={inputStyle(160)} />
            <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontFamily: F.body, fontSize: 12, color: C.inkSoft }}>
              <input type="checkbox" checked={onTrain} onChange={(e) => setOnTrain(e.target.checked)} /> On train
            </label>
            <button onClick={addLocation} style={miniBtn(C.sageDk)}>Add</button>
            <button onClick={() => setAdding(false)} style={miniBtn(C.inkMute)}>Cancel</button>
          </div>
        ) : (
          <button onClick={() => setAdding(true)} style={{ ...dashedAdd() }}>+ Add a new location</button>
        )}
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Small inputs
// ---------------------------------------------------------------------------

function ToggleRow({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 0" }}>
      <span style={{ fontFamily: F.body, fontWeight: 700, fontSize: 14, color: C.ink }}>{label}</span>
      <button onClick={() => onChange(!checked)} aria-pressed={checked} style={{ width: 46, height: 26, borderRadius: 999, border: "none", cursor: "pointer", background: checked ? C.sageDk : C.hairline, position: "relative", transition: "background 0.15s" }}>
        <span style={{ position: "absolute", top: 3, left: checked ? 23 : 3, width: 20, height: 20, borderRadius: "50%", background: "#fff", transition: "left 0.15s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }} />
      </button>
    </div>
  );
}

function NumberRow({ label, value, onChange }: { label: string; value: number; onChange: (v: number) => void }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 0" }}>
      <span style={{ fontFamily: F.body, fontWeight: 700, fontSize: 14, color: C.ink }}>{label}</span>
      <input type="number" value={value} onChange={(e) => onChange(Math.max(0, Math.floor(Number(e.target.value) || 0)))} style={inputStyle(90)} />
    </div>
  );
}

function TextRow({ label, value, onChange, mono }: { label: string; value: string; onChange: (v: string) => void; mono?: boolean }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "8px 0" }}>
      <span style={{ fontFamily: F.body, fontWeight: 700, fontSize: 14, color: C.ink }}>{label}</span>
      <input value={value} onChange={(e) => onChange(e.target.value)} style={{ ...inputStyle(180), fontFamily: mono ? F.mono : F.body }} />
    </div>
  );
}

function Divider() { return <div style={{ height: 1, background: C.hairlineLt, margin: "6px 0" }} />; }
function Toast({ text }: { text: string }) {
  return <div role="status" style={{ position: "fixed", left: "50%", transform: "translateX(-50%)", bottom: "calc(80px + env(safe-area-inset-bottom))", background: C.ink, color: "#FFFCEC", fontFamily: F.body, fontWeight: 700, fontSize: 13.5, padding: "10px 16px", borderRadius: 999, zIndex: 260 }}>{text}</div>;
}
function inputStyle(width: number): React.CSSProperties {
  return { width, boxSizing: "border-box", fontFamily: F.body, fontSize: 13, color: C.ink, border: `1px solid ${C.hairline}`, borderRadius: 8, padding: "6px 8px", background: "#fff" };
}
function selectStyle(): React.CSSProperties {
  return { fontFamily: F.body, fontSize: 12.5, color: C.ink, border: `1px solid ${C.hairline}`, borderRadius: 8, padding: "6px 8px", background: "#fff" };
}
function miniBtn(color: string): React.CSSProperties {
  return { fontFamily: F.body, fontSize: 12, fontWeight: 800, color, background: "transparent", border: `1px solid ${color}`, borderRadius: 8, padding: "6px 10px", cursor: "pointer" };
}
function dashedAdd(): React.CSSProperties {
  return { width: "100%", marginTop: 12, padding: "10px", border: `1.5px dashed ${C.sageLt}`, borderRadius: 12, background: "transparent", color: C.sageDk, fontFamily: F.body, fontWeight: 800, fontSize: 13.5, cursor: "pointer" };
}
function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}
